import { useEffect } from 'react'
import Chart from 'chart.js/auto'
import { PriceData } from '../../types/charts'
import { DateTime } from 'luxon'
import { ChartConfiguration, Plugin } from 'chart.js'
import { Box, Typography, useTheme } from '@mui/material'
import { lang } from '../../utils/lang'
import { Currency } from '../../types/models'
import { useTranslation } from 'react-i18next'

const FORECAST_COLOR = '#B8860B'

export interface PriceChartProps {
  id: string
  prices: PriceData[]
  currency: Currency
  vat?: number
}

export function PriceChart(props: PriceChartProps): React.ReactElement {
  const { id, prices, currency, vat } = props
  const theme = useTheme()
  const { t } = useTranslation()

  const hasForecast = prices.some(p => p.forecast)
  const isQuarter = prices.length > 48

  useEffect(() => {

    const itemsPerHour = isQuarter ? 4 : 1

    // Labels: show hour number at :00, empty for :15/:30/:45
    const labels = prices.map(p => {
      const dt = DateTime.fromISO(p.from).toLocal()
      if (isQuarter) {
        return dt.minute === 0 ? String(dt.hour) : ''
      }
      return String(dt.hour)
    })

    // Plugin: alternating 1-hour background bands
    const hourBandsPlugin: Plugin<'line'> = {
      id: 'hourBands',
      beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart
        if (!chartArea) return
        const xScale = scales.x
        const totalHours = Math.ceil(prices.length / itemsPerHour)
        const stepPx = xScale.getPixelForValue(1) - xScale.getPixelForValue(0)
        const halfStep = stepPx / 2

        for (let h = 0; h < totalHours; h++) {
          if (h % 2 === 0) continue
          const startIdx = h * itemsPerHour
          const endIdx = Math.min((h + 1) * itemsPerHour, prices.length) - 1
          const x1 = xScale.getPixelForValue(startIdx) - halfStep
          const x2 = xScale.getPixelForValue(endIdx) + halfStep

          ctx.save()
          ctx.fillStyle = 'rgba(255, 255, 255, 0.04)'
          ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
          ctx.restore()
        }
      }
    }

    const applyVat = (price: number | null | undefined): number | null => {
      if (price === null || price === undefined) return null
      return vat ? price * (1 + vat) : price
    }

    const config: ChartConfiguration = {
      type: 'line',

      plugins: [hourBandsPlugin],

      options: {
        animation: false,

        layout: {
          padding: {
            top: 20
          }
        },

        plugins: {
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            filter: (item) => item.raw !== null && item.raw !== undefined,
            callbacks: {
              title: (items) => {
                if (!items.length) return ''
                const idx = items[0].dataIndex
                if (idx < prices.length) {
                  const dt = DateTime.fromISO(prices[idx].from).toLocal()
                  return dt.toFormat('HH:mm')
                }
                return ''
              },
              label: (item) => {
                if (item.raw === null || item.raw === undefined) return ''
                const value = (item.raw as number).toFixed(2)
                return ` ${value} ${lang.moneyShortUnit(currency)}`
              }
            }
          },
          legend: {
            display: false
          },
        },

        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              autoSkip: false,
              font: {
                size: 10
              },
            },
          },
          y: {
            suggestedMin: 0,
            suggestedMax: 25,
            grid: {
              color: '#dfdfdf',
              lineWidth: 0.075
            },
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              callback: (value) => lang.moneyShort(value as number, currency)
            }
          }
        },
      },

      data: {
        labels,
        datasets: [
          {
            data: prices.map(e => !e.forecast && e.price !== null && e.price !== undefined
              ? applyVat(e.price)
              : null),
            pointRadius: 0,
            borderWidth: 2,
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            showLine: true,
            stepped: 'middle',
          },
          {
            data: prices.map(e => e.forecast && e.price !== null && e.price !== undefined
              ? applyVat(e.price)
              : null),
            pointRadius: 0,
            borderWidth: 2,
            backgroundColor: FORECAST_COLOR,
            borderColor: FORECAST_COLOR,
            showLine: true,
            stepped: 'middle',
          },
        ]
      },
    }

    const myChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config
    )

    return () => {
      myChart.destroy()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices])

  return (
    <Box>
      <canvas style={{ display: 'block', width: '100%' }} id={id}/>
      {hasForecast && (
        <Box sx={{ mt: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 24, height: 2, backgroundColor: theme.palette.primary.main, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: theme.palette.grey[400], fontSize: '0.7rem' }}>
              {t('Confirmed spot prices')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 24, height: 2, backgroundColor: FORECAST_COLOR, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: theme.palette.grey[400], fontSize: '0.7rem' }}>
              {t('Spot price forecast')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}
